/* eslint-disable import/no-duplicates */
import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';

import { itemEditRoles, itemGroupTypeOptions, ItemListFilters, ItemModel } from '../../../../api/core/core.types';
import Select from '../../../../components/forms/Select';
import StatusLabel from '../../../../components/forms/StatusLabel';
import { getItemList, updateItemFilters } from '../../actions/item.actions';
import { ItemActionTypes } from '../../types/item.types';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';
import { WithTranslation, withTranslation } from 'react-i18next';
import ListViewTable from '../../../../components/tables/ListViewTable';

interface ItemProps extends RouteComponentProps, WithTranslation {
  getItemList: (filters?: ItemListFilters, page?: PageRequest) => void;
  updateItemFilters: (filters: ItemListFilters) => void;
  items: ItemModel[] | null;
  user: UserModel | null;
  totalElements: number;
  totalPages: number;
  filters: ItemListFilters | null;
}

interface ItemState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string;
  filtersUpdated: boolean;
  filter: {
    combinedFilter: {
      value: string;
    };
    itemGroupTypeFilter: {
      value: { label: string; value: string }[];
    };
  };
}

const ItemSortFields = [
  { value: 'otaCode', label: 'OTA_CODE' },
  { value: 'name', label: 'NAME' }
];

class Items extends React.Component<ItemProps, ItemState> {
  constructor(props: ItemProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'ASC',
      sortedBy: 'NAME',
      filtersUpdated: false,
      filter: {
        combinedFilter: {
          value: ''
        },
        itemGroupTypeFilter: {
          value: []
        }
      }
    };
  }

  componentDidMount() {
    this.updateFilterValues();
    this.props.getItemList(this.props.filters ? this.props.filters : undefined);
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) this.updateFilterValues();
  }

  onRowClick = (rowInfo: any) =>
    this.props.history.push(`/setup/items/${rowInfo.original.id}/view`);


  statusCell = (props: any) => (<StatusLabel status={props}/>);

  onFilterClick = (e: FormEvent) => {
    e.preventDefault();
    const filters = this.composeItemFilters();
    this.props.updateItemFilters(filters);
    this.props.getItemList(filters);
  };

  updateFilterValues = () => {
    const filters = this.props.filters;
    if (filters) {
      this.setState({
        selectedPageNumber: filters.pageNumber || 1,
        filtersUpdated: true,
        filter: {
          combinedFilter: {
            value: filters.combinedFilterField ? filters.combinedFilterField : ''
          },
          itemGroupTypeFilter: {
            value: filters.type ? [ { value: filters.type, label: filters.type } ] : []
          }
        }
      });
    }
  };

  clearFilters = async () => {
    await this.setState({
      selectedPageNumber: 1,
      filtersUpdated: true,
      filter: {
        combinedFilter: {
          value: ''
        },
        itemGroupTypeFilter: {
          value: []
        }
      }
    });

    const filters = this.composeItemFilters();
    this.props.updateItemFilters(filters);
    this.props.getItemList();
  };

  composeItemFilters = () => {
    const type: any = this.state.filter.itemGroupTypeFilter.value;

    return {
      combinedFilterField: this.state.filter.combinedFilter.value,
      type: type ? type.value : undefined,
      pageNumber: this.state.selectedPageNumber - 1
    };
  };

  handleUpdateResults = () => {
    const filters = this.composeItemFilters();
    this.props.updateItemFilters(filters);
    this.props.getItemList(filters);
  };

  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = (newValue: any, field: string) => {
    this.setState({
      ...this.state,
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    }, () => {
      this.handleUpdateResults();
    });
  };

  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.items.name'), accessorKey: 'name', id: 'name'
      },
      {
        header: t('ct.items.ota_code'), accessorKey: 'otaCode', id: 'otaCode'
      },
      {
        header: t('ct.items.group_type'), accessorKey: 'itemGroupType', id: 'itemGroupType'
      },
      { header: t('ct.items.max_quantity'), accessorKey: 'maxQuantity', id: 'maxQuantity' },
      {
        header: t('ct.items.status'),
        accessorKey: 'statusType',
        id: 'statusType',
        cell: (info: any) => this.statusCell(info.getValue())
      }
    ];
  }

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    const sortFieldName = ItemSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: sortFieldName.label });
    return (
      this.props.getItemList(this.composeItemFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortField !== undefined && pageRequest.sortDirection !== undefined) {
      const pageRequestFix = {
        page: pageRequest.page,
        sortDirection: pageRequest.sortDirection,
        sortField: ItemSortFields.find((column) => column.label === pageRequest.sortField)?.label
      };
      this.props.getItemList(this.composeItemFilters(), pageRequestFix);

      this.setState({
        selectedPageNumber: pageRequestFix.page + 1,
        sortOrder: pageRequestFix.sortDirection ?? 'ASC',
        sortedBy: ItemSortFields.find((column) => column.label === pageRequest.sortField)?.label ?? 'CODE'
      });
    }
  }

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>{t('page_title.items')}</h1>
            {this.props.user && userCanAccessComponent(this.props.user, itemEditRoles) && (
              <Link
                to={{
                  pathname: '/setup/items/create'
                }}
                className="btn btn-primary btn-wide"
              >
                {t('b.add_new').toUpperCase()}
              </Link>
            )}
          </div>
          <form className="d-flex justify-content-between container-fluid filter-container form-group form-inline">
            <div className="d-flex flex-wrap w-75 filters-container-margin-top-btm">
              <input
                value={this.state.filter.combinedFilter.value}
                type="text"
                name="combinedFilter"
                className="form-control col edit-form-input margins pl-1 ml-1 min-width-form-input"
                placeholder={t('ph.filter.items.by_name')}
                onChange={this.changeHandler}
              />
              <Select
                value={this.state.filter.itemGroupTypeFilter.value}
                className="mr-1"
                placeholder={t('ph.filter.items.by_group')}
                options={itemGroupTypeOptions}
                equalWidthElement
                onChange={(val) => this.dropdownChange(val, 'itemGroupTypeFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="status"
              />
            </div>
            <div>
              <button type="button" className="btn btn-text-only mr-2" onClick={() => this.clearFilters()}>
                {t('b.clear_filter')}</button>
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>
                {t('b.filter').toUpperCase()}</button>
            </div>
          </form>
          {this.props.items
            ? (
              <div className="table-list">
                <ListViewTable
                  data={this.props.items}
                  columns={this.defineColumns()}
                  getTrProps={this.onRowClick}
                  sortBy={this.sortBy}
                  sortOrder={this.state.sortOrder}
                  sortedBy={ItemSortFields.find(({ value, label }) => this.state.sortedBy === label)?.value}
                  paginationEnabled={true}
                  totalPages={this.props.totalPages}
                  totalElements={this.props.totalElements}
                  selectedPageNumber={this.state.selectedPageNumber}
                  getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
                />
              </div>
            ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  items: state.setup.items.items,
  user: state.authentication.user,
  totalElements: state.setup.items.totalElements,
  totalPages: state.setup.items.totalPages,
  filters: state.setup.items.filters
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, ItemActionTypes>) => ({
  getItemList: (filters?: ItemListFilters, page?: PageRequest) => dispatch(getItemList(filters, page)),
  updateItemFilters: (filters: ItemListFilters) => dispatch(updateItemFilters(filters))
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(Items));
